import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import * as HTML5BackendModule from 'react-dnd-html5-backend';

const ItemType = 'TOKEN_ITEM';
const TOKEN_FONT_SIZE = 22;
const TOKEN_BUTTON_STYLE = {
  minWidth: 48,
  fontSize: TOKEN_FONT_SIZE,
  height: 48,
  lineHeight: '20px',
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #D9D8FF',
  background: '#D9D8FF',
  color: '#043967',
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const EMPTY_BOX_STYLE = {
  ...TOKEN_BUTTON_STYLE,
  background: 'white',
  border: '1px solid #91C2F9',
  padding: 0,
};
function DraggableToken({
  token, from, index, compact = false,
}) {
  const tokenStyle = compact
    ? {
      ...TOKEN_BUTTON_STYLE,
      background: '#37AFAD',
      border: '1px solid #37AFAD',
      color: '#FFFFFF',
      width: '100%',
      minWidth: '100%',
      height: '100%',
      boxSizing: 'border-box',
    }
    : TOKEN_BUTTON_STYLE;

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: token.id, from, index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });

  return (
    <div
      ref={drag}
      className={isDragging ? 'opacity-50' : ''}
      style={tokenStyle}
    >
      <span style={{ fontSize: TOKEN_FONT_SIZE }}>{token.text}</span>
    </div>
  );
}

function InlineSlot({
  slotId,
  placedToken,
  onDropToken,
  placeholder,
}) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    collect: (m) => ({ isOver: m.isOver() }),
    drop: (dragged) => onDropToken(dragged, slotId),
  });

  // neutral border: avoid showing correctness
  const boxBorder = placedToken ? '1px solid transparent' : '1px solid #91C2F9';

  return (
    <span
      ref={drop}
      style={{
        ...EMPTY_BOX_STYLE,
        border: boxBorder,
        position: 'relative',
        display: 'inline-flex',
        width: TOKEN_BUTTON_STYLE.minWidth,
        height: TOKEN_BUTTON_STYLE.height,
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isOver && (
        <div style={{ position: 'absolute', pointerEvents: 'none' }} />
      )}
      {placedToken ? (
        <DraggableToken token={placedToken} from={slotId} compact />
      ) : (
        <>
          <div style={{
            position: 'absolute', left: 8, right: 8, top: '50%', transform: 'translateY(-50%)', borderTop: '1px dashed #dcdfe3', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: 8, bottom: 8, left: '50%', transform: 'translateX(-50%)', borderLeft: '1px dashed #dcdfe3', pointerEvents: 'none',
          }} />
        </>
      )}
    </span>
  );
}

function SourceDropArea({ children, onReturn }) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    collect: (m) => ({ isOver: m.isOver() }),
    drop: (dragged) => onReturn(dragged),
  });

  return (
    <div ref={drop} style={{ position: 'relative' }}>
      {isOver && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.02)',
            borderRadius: 6,
          }}
        />
      )}
      {children}
    </div>
  );
}

export default function SentenceCompletion({
  id,
  items = [],
  sentence = [],
  answers = [],
  initialPlacement = {},
  value = null,
  onChange = () => {},
  restrictEdit = false,
  resetSignal,
  showReset = true,
  showAns = false,
}) {
  // sentence: array of objects representing words or slots; slots have { id, placeholder }

  const resolveToken = (val, tokenList) => {
    if (!val) return null;
    if (typeof val === 'string') return tokenList.find((t) => t.id === val) || null;
    if (typeof val === 'object') {
      const found = tokenList.find((t) => t.id === val.id);
      return found || val;
    }
    return null;
  };

  const computeInitial = useCallback(() => {
    const placedIds = new Set();
    const place = {};
    const sourcePlacement = value || initialPlacement;
    sentence.forEach((s) => {
      if (!s || !s.id) return;
      const pid = sourcePlacement
        && Object.prototype.hasOwnProperty.call(sourcePlacement, s.id)
        ? sourcePlacement[s.id]
        : null;
      const resolved = resolveToken(pid, items);
      place[s.id] = resolved;
      if (resolved && resolved.id) placedIds.add(resolved.id);
    });
    const src = items.filter((it) => !placedIds.has(it.id));
    return { place, src };
  }, [items, sentence, initialPlacement, value]);

  const initial = useMemo(() => computeInitial(), [computeInitial]);
  const [sourceTokens, setSourceTokens] = useState(initial.src);
  const [placement, setPlacement] = useState(initial.place);

  const completedSentence = useMemo(() => {
    if (!Array.isArray(sentence)) return '';
    // Build list of original slot indices (either provided in seg.expectedIndex
    // or inferred from the slot id like 's0' -> 0) and remember their positions in
    // the sentence array. This lets us detect contiguous ranges and also avoid
    // inserting a comma when the original `sentence` already contains punctuation
    // between slots (prevents double commas).
    const slotOrigIndices = [];
    const slotPositions = [];
    sentence.forEach((seg, idx) => {
      if (seg && seg.type === 'slot') {
        const orig = (typeof seg.expectedIndex === 'number')
          ? seg.expectedIndex
          : (Number(String(seg.id).replace(/^s/, '')) || 0);
        slotOrigIndices.push(orig);
        slotPositions.push(idx);
      }
    });

    // If any placed slot is empty, show empty preview.
    const parts = [];
    let slotCounter = 0;
    for (let i = 0; i < sentence.length; i += 1) {
      const seg = sentence[i];
      if (seg && seg.type === 'slot') {
        const p = placement[seg.id];
        if (!p) return '';
        const pid = typeof p === 'string' ? p : p.id;
        const found = (items || []).find((t) => t.id === pid)
          || (sourceTokens || []).find((t) => t.id === pid);
        const text = found ? found.text : (typeof p === 'string' ? p : (p && p.text) || '');
        parts.push(text);

        // check boundary to next slot: insert comma if next slot's orig index is not contiguous
        // but only if original sentence does not already contain punctuation between these two slots
        const nextSlotOrig = slotOrigIndices[slotCounter + 1];
        const thisOrig = slotOrigIndices[slotCounter];
        if (typeof nextSlotOrig !== 'undefined' && typeof thisOrig === 'number') {
          if (nextSlotOrig !== thisOrig + 1) {
            // find positions in the sentence array
            const thisPos = slotPositions[slotCounter];
            const nextPos = slotPositions[slotCounter + 1];
            // examine any intervening segments for existing punctuation
            let interveningHasPunct = false;
            for (let k = thisPos + 1; k < nextPos; k += 1) {
              const mid = sentence[k];
              if (mid && typeof mid.text === 'string' && /[，。､､,.;:]/.test(mid.text)) {
                interveningHasPunct = true;
                break;
              }
            }
            if (!interveningHasPunct) parts.push('，');
          }
        }
        slotCounter += 1;
      } else {
        parts.push(seg && seg.text ? seg.text : '');
      }
    }

    return parts.join('');
  }, [placement, sentence, items, sourceTokens]);

  useEffect(() => {
    if (value === null) return;
    const { place, src } = computeInitial();
    setPlacement(place);
    setSourceTokens(src);
  }, [items, sentence, initialPlacement, value]);

  const commitChange = useCallback(
    (newPlacement, newSource) => {
      setPlacement(newPlacement);
      setSourceTokens(newSource);
      const report = { placement: {}, source: newSource.map((s) => s.id) };
      Object.keys(newPlacement).forEach((k) => {
        report.placement[k] = newPlacement[k] ? newPlacement[k].id : null;
      });
      // compute completed sentence string from user's placements only
      const parts = sentence.map((seg) => {
        if (seg && seg.type === 'slot') {
          const p = newPlacement[seg.id];
          if (!p) return null;
          const pid = typeof p === 'string' ? p : p.id;
          const found = (newSource || []).find((t) => t.id === pid)
            || (items || []).find((t) => t.id === pid);
          return found
            ? found.text
            : typeof p === 'string'
              ? p
              : (p && p.text) || '';
        }
        return seg && seg.text ? seg.text : '';
      });
      report.completedSentence = parts.some((p) => p === null)
        ? ''
        : parts.join('');
      // also include slotAnswers (texts) in the report
      const slotSegments = sentence.filter((s) => s && s.type === 'slot');
      const indexedSlots = slotSegments.every(
        (s) => typeof s.answerIndex === 'number' && !Number.isNaN(s.answerIndex),
      );

      if (indexedSlots && slotSegments.length > 0) {
        const maxIdx = Math.max(...slotSegments.map((s) => s.answerIndex));
        const slotAnswers = Array(maxIdx + 1).fill('');
        slotSegments.forEach((s) => {
          const p = newPlacement[s.id];
          if (!p) return;
          const pid = typeof p === 'string' ? p : p.id;
          const found = (newSource || []).find((t) => t.id === pid)
            || (items || []).find((t) => t.id === pid);
          slotAnswers[s.answerIndex] = found ? found.text : pid;
        });
        report.slotAnswers = slotAnswers;
      } else {
        const slotAnswers = slotSegments.map((s) => {
          const p = newPlacement[s.id];
          if (!p) return '';
          const pid = typeof p === 'string' ? p : p.id;
          const found = (newSource || []).find((t) => t.id === pid)
            || (items || []).find((t) => t.id === pid);
          return found ? found.text : pid;
        });
        report.slotAnswers = slotAnswers;
      }
      // defer onChange to avoid triggering parent re-renders during DnD lifecycle
      if (onChange) setTimeout(() => onChange(report), 0);
    },
    [onChange, sentence, items],
  );

  const handleDrop = useCallback(
    (dragged, toSlotId) => {
      if (restrictEdit) return;
      setPlacement((prev) => {
        const newPlacement = { ...prev };
        const newSource = [...sourceTokens];

        if (dragged.from === 'source') {
          const idx = newSource.findIndex((s) => s.id === dragged.id);
          if (idx !== -1) newSource.splice(idx, 1);
        } else {
          newPlacement[dragged.from] = null;
        }

        const existing = newPlacement[toSlotId];
        if (existing) newSource.unshift(existing);

        const draggedToken = items.find((it) => it.id === dragged.id) || {
          id: dragged.id,
          text: dragged.id,
        };
        newPlacement[toSlotId] = draggedToken;

        commitChange(newPlacement, newSource);
        return newPlacement;
      });
    },
    [items, sourceTokens, restrictEdit, commitChange],
  );

  const handleReturnToSource = useCallback(
    (dragged) => {
      if (restrictEdit) return;
      setPlacement((prev) => {
        const newPlacement = { ...prev };
        const newSource = [...sourceTokens];
        if (dragged.from !== 'source') {
          const p = newPlacement[dragged.from];
          if (p && p.id === dragged.id) {
            newSource.unshift(p);
            newPlacement[dragged.from] = null;
          }
        }
        commitChange(newPlacement, newSource);
        return newPlacement;
      });
    },
    [sourceTokens, restrictEdit, commitChange],
  );

  const handleReset = useCallback(() => {
    if (restrictEdit) return;
    const place = {};
    sentence.forEach((s) => {
      if (s && s.type === 'slot' && s.id) place[s.id] = null;
    });
    const src = [...items];
    commitChange(place, src);
  }, [sentence, items, restrictEdit, commitChange]);

  React.useEffect(() => {
    if (typeof resetSignal !== 'undefined') {
      handleReset();
    }
  }, [resetSignal]);

  const slotAnswers = useMemo(
    () => sentence
      .filter((s) => s && s.type === 'slot')
      .map((s) => {
        const p = placement[s.id];
        if (!p) return '';
        const pid = typeof p === 'string' ? p : p.id;
        const found = (sourceTokens || []).find((t) => t.id === pid)
            || (items || []).find((t) => t.id === pid);
        return found ? found.text : pid;
      }),
    [placement, sentence, sourceTokens, items],
  );

  const allSlotsFilled = useMemo(() => {
    if (!Array.isArray(sentence)) return false;
    const slots = sentence.filter((s) => s && s.type === 'slot');
    if (slots.length === 0) return false;
    return slots.every((s) => placement && placement[s.id]);
  }, [sentence, placement]);

  const ResolvedBackend = HTML5BackendModule.default
    || HTML5BackendModule.HTML5Backend
    || HTML5BackendModule;

  return (
    <DndProvider backend={ResolvedBackend}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            fontSize: 'clamp(14px, 2.2vw, 18px)',
            lineHeight: 1.6,
          }}
        >
          {sentence.map((seg, i) => {
            if (seg && seg.type === 'slot') {
              return (
                <InlineSlot
                  key={seg.id}
                  slotId={seg.id}
                  placedToken={placement[seg.id]}
                  onDropToken={handleDrop}
                  placeholder={seg.placeholder}
                />
              );
            }

            const text = seg && seg.text ? seg.text : '';
            const chunks = String(text).split(/([,，])/);

            return chunks.map((chunk, idx) => {
              if (!chunk) return null;
              const isComma = chunk === ',' || chunk === '，';
              if (isComma) {
                return (
                  <React.Fragment key={`comma-${i}-${idx}`}>
                    <span style={{ margin: '0 6px' }}>{chunk}</span>
                    <span style={{ flexBasis: '100%', height: 0 }} />
                  </React.Fragment>
                );
              }
              return (
                <span key={`text-${i}-${idx}`} style={{ margin: '0 6px' }}>
                  {chunk}
                </span>
              );
            });
          })}
        </div>
        <div style={{ width: '100%' }}>
          <SourceDropArea onReturn={handleReturnToSource}>
            <div
              style={{
                padding: 12,
                minHeight: 60,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
              }}
            >
              {sourceTokens.length === 0 && (
                <div style={{ color: '#888' }}>No available words</div>
              )}
              {sourceTokens.map((t, idx) => (
                <DraggableToken
                  key={t.id}
                  token={t}
                  from="source"
                  index={idx}
                />
              ))}
            </div>
          </SourceDropArea>
        </div>
        {showReset !== false && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleReset}
            >
              重設
            </button>
          </div>
        )}
        {showAns && allSlotsFilled && (
          <div className="mt-2">
            <div className="p-2" style={{ background: '#F9D78B', border: '2px solid #F9D78B', borderRadius: 4 }}>
              <strong className="text-dark">我的答案：</strong>
              <span className="ml-2">{completedSentence}</span>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
}
